import React, {
    memo,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { useParams } from 'react-router-dom';
import { List } from 'immutable';
import { Menu, MenuItem, MenuItems } from '@headlessui/react';

import { sendChatMessage } from 'flow/actions/chat-actions';
import ClickAwayListener from 'flow/components/ClickAwayListener';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';
import SearchInput from 'flow/components/ui-components/SearchInput';
import { useModal } from 'flow/contexts/ModalProvider';
import {
    useDeletePremadeAnswer,
    usePremadeAnswers as usePremadeAnswersApi,
} from 'flow/hooks/api/usePremadeAnswer';
import useAppContext from 'flow/hooks/useAppContext';
import usePremadeAnswers from 'flow/hooks/usePremadeAnswers';
import { selectOwnUserHasCapability } from 'flow/selectors/ExtensionSelectors';
import { getOwnExtensionKey } from 'flow/selectors/selectors';
import { ChatSessionKey } from 'flow/types/EntityKeys';

import { Capabilities } from 'flow/utils/capability-utils';
import { twMerge } from 'tailwind-merge';

interface Props {
    message: string;
    updateMessage: (update: string, cursorPosition: number) => void;
}

const PremadeAnswersManager = ({ message, updateMessage }: Props) => {
    const { sendNotification, notification, i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const { openModal } = useModal();
    const chatSessionKey = useParams<{ id: ChatSessionKey }>()?.id;
    const { substitutePlaceholders } = usePremadeAnswers();
    const { data: templates } = usePremadeAnswersApi();
    const deletePremadeAnswer = useDeletePremadeAnswer();

    const ownExtensionKey = useAppSelector(getOwnExtensionKey);
    const hasSmsCapability = useAppSelector((state) =>
        selectOwnUserHasCapability(state, Capabilities.SMS),
    );
    const session = useAppSelector((state) =>
        state.chats.getIn(['sessions', chatSessionKey]),
    );

    const [search, setSearch] = useState('');
    const [deleted, setDeleted] = useState<string[]>([]);
    const [premadeAnswerPopup, setPremadeAnswerPopup] = useState(false);

    let contact = null;
    if (session?.get('members', List()).size === 2) {
        contact = session
            .get('members')
            .find((member) => member.get('extensionKey') !== ownExtensionKey);
    }

    const clientKey = useAppSelector((state) =>
        state.chats.getIn(['client', 'key']),
    );

    const receiver = useAppSelector((state) =>
        state.extensions.getIn([
            'extensions',
            contact?.get('extensionKey'),
            'contact',
        ]),
    );

    const filteredAnswers = useMemo(
        () =>
            templates?.filter(
                (template) =>
                    (!template.type || template.type === 'PREMADE_ANSWER') &&
                    !deleted.includes(template.key) &&
                    template.name
                        .toLowerCase()
                        .startsWith(search.toLowerCase()),
            ) || [],
        [templates, search, deleted],
    );

    // Open when we get a notification and focus text input, making sure any previous
    // search is cleared.
    const searchRef = useRef<HTMLInputElement | null>();
    const open = () => {
        setSearch('');
        setPremadeAnswerPopup(true);
        setTimeout(() => searchRef.current?.focus(), 0);
    };
    useEffect(() => {
        if (notification?.type === 'open-premade-answers') {
            open();
        }
    }, [notification]);

    useEffect(() => {
        return () => {
            setPremadeAnswerPopup(false);
            sendNotification(null);
        };
    }, []);

    // When a message is chosen, insert it after any existing text; this also sets the
    // focus back on the message text input.
    const handleChooseMessage = useCallback(
        (premadeAnswer: string) => {
            const newText = message + substitutePlaceholders(premadeAnswer);
            updateMessage(newText, newText.length);
            setSearch('');
        },
        [message, substitutePlaceholders],
    );

    const addButtonRef = useRef<HTMLButtonElement | null>();

    return !premadeAnswerPopup ? null : (
        <ClickAwayListener onClickAway={() => setPremadeAnswerPopup(false)}>
            <Menu>
                <MenuItems
                    static={true}
                    as="div"
                    className="absolute start-3 top-0 z-40 w-full -translate-y-full pe-8 outline-hidden"
                >
                    <div className="flex h-[60dvh] w-full flex-col gap-3 rounded-sm bg-white p-2 shadow-lg dark:bg-gray-100">
                        <div className="flex gap-3">
                            <SearchInput
                                iconKey="search"
                                onChange={(event) =>
                                    setSearch(event.target.value)
                                }
                                onKeyDown={(e) => {
                                    switch (e.key) {
                                        case 'Enter':
                                            if (filteredAnswers.length > 0) {
                                                handleChooseMessage(
                                                    filteredAnswers[0].message,
                                                );
                                            }
                                            break;
                                        case 'Tab':
                                            e.preventDefault();
                                            e.stopPropagation();
                                            addButtonRef.current?.focus();
                                            break;
                                        case 'Escape':
                                            setPremadeAnswerPopup(false);
                                            break;
                                    }
                                }}
                                value={search}
                                ref={searchRef}
                                autoFocus={true}
                            />

                            <button
                                type="button"
                                aria-label={i18next.t('chatsettings.add')}
                                className="hover:darken-10 focus-ring size-10 items-center rounded-sm bg-gray-200 text-gray-600 hover:text-gray-700"
                                onClick={() =>
                                    openModal('premadeAnswer', {
                                        chatSessionKey,
                                        type: 'PREMADE_ANSWER',
                                        onClose: open,
                                    })
                                }
                                ref={addButtonRef}
                            >
                                <Icon iconKey="add" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto">
                            {filteredAnswers
                                .sort((a, b) => (a.name < b.name ? -1 : 1))
                                .map((answer) => (
                                    <MenuItem key={answer.key}>
                                        {({ focus }) => (
                                            <div
                                                className={twMerge(
                                                    'group flex w-full items-center justify-between rounded-sm p-2 text-start hover:bg-gray-200',
                                                    focus ? 'bg-gray-200' : '',
                                                    notification?.edit
                                                        ? ''
                                                        : 'cursor-pointer',
                                                )}
                                                onClick={() => {
                                                    if (!notification?.edit) {
                                                        handleChooseMessage(
                                                            answer.message,
                                                        );

                                                        setPremadeAnswerPopup(
                                                            false,
                                                        );
                                                    }
                                                }}
                                            >
                                                <div className="flex w-10/12 flex-col">
                                                    <p className="text-sm/5 text-gray-800">
                                                        {answer.name}
                                                    </p>
                                                    <p className="truncate overflow-hidden text-xs/4 whitespace-nowrap text-gray-500">
                                                        {notification?.edit
                                                            ? answer.message
                                                            : substitutePlaceholders(
                                                                  answer.message,
                                                              )}
                                                    </p>
                                                </div>
                                                {notification?.edit && (
                                                    <div className="hidden gap-2 group-hover:flex">
                                                        <IconButton
                                                            aria-label={i18next.t(
                                                                'Tooltip.Nav.Title.Edit',
                                                            )}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                openModal(
                                                                    'premadeAnswer',
                                                                    {
                                                                        premadeAnswer:
                                                                            answer,
                                                                        type: 'PREMADE_ANSWER',
                                                                        onClose:
                                                                            open,
                                                                    },
                                                                );
                                                            }}
                                                        >
                                                            <Icon iconKey="edit" />
                                                        </IconButton>
                                                        <IconButton
                                                            aria-label={i18next.t(
                                                                'Delete.Button.Text',
                                                            )}
                                                            onClick={(e) => {
                                                                deletePremadeAnswer.mutate(
                                                                    {
                                                                        key: answer.key,
                                                                    },
                                                                );
                                                                setDeleted([
                                                                    ...deleted,
                                                                    answer.key,
                                                                ]);
                                                                e.stopPropagation();
                                                            }}
                                                        >
                                                            <Icon iconKey="delete" />
                                                        </IconButton>
                                                    </div>
                                                )}
                                                {!notification?.edit && (
                                                    <div className="hidden gap-2 group-hover:flex">
                                                        {hasSmsCapability && (
                                                            <IconButton
                                                                tooltipLabel={i18next.t(
                                                                    'Chat.Tooltip.SMS',
                                                                )}
                                                                onClick={(
                                                                    event,
                                                                ) => {
                                                                    event.stopPropagation();
                                                                    dispatch(
                                                                        sendChatMessage(
                                                                            chatSessionKey,
                                                                            clientKey,
                                                                            substitutePlaceholders(
                                                                                answer.message,
                                                                            ),
                                                                            null,
                                                                            null,
                                                                            true,
                                                                        ),
                                                                    );
                                                                    setPremadeAnswerPopup(
                                                                        false,
                                                                    );
                                                                }}
                                                            >
                                                                <Icon iconKey="sms" />
                                                            </IconButton>
                                                        )}

                                                        {receiver?.get(
                                                            'email',
                                                        ) && (
                                                            <IconButton
                                                                tooltipLabel={i18next.t(
                                                                    'Chat.Tooltip.Email',
                                                                )}
                                                                onClick={(
                                                                    e,
                                                                ) => {
                                                                    window.open(
                                                                        `mailto:${receiver.get('email')}?subject=${encodeURIComponent(answer.name)}&body=${encodeURIComponent(
                                                                            substitutePlaceholders(
                                                                                answer.message,
                                                                            ),
                                                                        )}`,
                                                                        '_blank',
                                                                    );
                                                                    e.stopPropagation();
                                                                }}
                                                            >
                                                                <Icon iconKey="email" />
                                                            </IconButton>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </MenuItem>
                                ))}
                        </div>
                    </div>
                </MenuItems>
            </Menu>
        </ClickAwayListener>
    );
};

export default memo(PremadeAnswersManager);
